import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Grid,
  Dialog,
  TextField,
  IconButton,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Poll as PollIcon
} from '@mui/icons-material';
import { Event, Feedback } from '@/types/events';
import { v4 as uuidv4 } from 'uuid';

interface FeedbackSystemProps {
  event: Event;
  onUpdate: (updatedEvent: Event) => void;
}

export const FeedbackSystem: React.FC<FeedbackSystemProps> = ({
  event,
  onUpdate
}) => {
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleAddFeedback = () => {
    setSelectedFeedback({
      id: uuidv4(),
      type: 'pre',
      responses: []
    });
    setIsDialogOpen(true);
  };

  const handleSaveFeedback = (feedback: Feedback) => {
    const updatedFeedback = selectedFeedback?.id
      ? event.feedback.map(f => f.id === feedback.id ? feedback : f)
      : [...event.feedback, feedback];
    onUpdate({ ...event, feedback: updatedFeedback });
    setIsDialogOpen(false);
    setSelectedFeedback(null);
  };

  const handleDeleteFeedback = (feedbackId: string) => {
    const updatedFeedback = event.feedback.filter(f => f.id !== feedbackId);
    onUpdate({ ...event, feedback: updatedFeedback });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Feedback System</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddFeedback}
          >
            Create Survey
          </Button>
        </Stack>

        <Grid container spacing={3}>
          {event.feedback.map((feedback) => (
            <Grid item xs={12} md={6} key={feedback.id}>
              <Paper sx={{ p: 2 }}>
                <Stack spacing={2}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <PollIcon color="primary" />
                      <Typography variant="subtitle1">
                        {feedback.type === 'pre' ? 'Pre-Event Survey' : 'Post-Event Survey'}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1}>
                      <IconButton
                        size="small"
                        onClick={() => setSelectedFeedback(feedback)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteFeedback(feedback.id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </Stack>

                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Responses
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Chip
                        label={`${feedback.responses.length} responses`}
                        size="small"
                      />
                      <Chip
                        label={`Last response: ${
                          feedback.responses.length > 0
                            ? new Date(
                                feedback.responses[feedback.responses.length - 1].timestamp
                              ).toLocaleDateString()
                            : 'No responses'
                        }`}
                        size="small"
                        variant="outlined"
                      />
                    </Stack>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Stack>

      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {selectedFeedback?.id ? 'Edit Survey' : 'Create Survey'}
          </Typography>
          <Stack spacing={2}>
            <TextField
              select
              label="Survey Type"
              value={selectedFeedback?.type || 'pre'}
              onChange={(e) => setSelectedFeedback(prev => 
                prev ? { ...prev, type: e.target.value as 'pre' | 'post' } : null
              )}
              fullWidth
            >
              <option value="pre">Pre-Event Survey</option>
              <option value="post">Post-Event Survey</option>
            </TextField>

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => selectedFeedback && handleSaveFeedback(selectedFeedback)}
              >
                Save
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Dialog>
    </Box>
  );
};